import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Wipe all per-user cached API data + map/weather tiles so a new session never
// sees the previous user's dashboard, profile, location or map tiles.
async function clearAppCaches() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.filter((k) => k.startsWith("mg-data") || k.startsWith("mg-tiles")).map((k) => caches.delete(k))
      );
    }
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "CLEAR_USER_CACHE" });
    }
  } catch { /* best-effort */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("mg_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("mg_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password, remember = false) => {
    await clearAppCaches(); // drop any prior user's cached data before this session
    const { data } = await api.post("/auth/login", { email, password, remember });
    localStorage.setItem("mg_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    await clearAppCaches();
    const { data } = await api.post("/auth/register", payload);
    // New users must verify their email before logging in — no token is returned.
    return data;
  };

  // Used by the team-invite accept flow: the backend already returned an app JWT
  // + user, so we just adopt the session (fresh caches for the new account).
  const sessionLogin = async (token, userObj) => {
    await clearAppCaches();
    localStorage.setItem("mg_token", token);
    setUser(userObj);
    return userObj;
  };

  const logout = async () => {
    localStorage.removeItem("mg_token");
    setUser(false);
    await clearAppCaches(); // clear cached user data, location, map tiles & weather
  };

  const refreshUser = loadUser;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, sessionLogin, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
